import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import TextField from '@mui/material/TextField'
import Collapse from '@mui/material/Collapse'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import FilterListIcon from '@mui/icons-material/FilterList'
import RefreshIcon from '@mui/icons-material/Refresh'
import StarIcon from '@mui/icons-material/Star'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import CameraService from '../../services/CameraService'
import CameraProvisionStatus from './CameraProvisionStatus'

interface Room {
  id: number
  name: string
}

interface Camera {
  id: number
  name: string
  ip: string
  port: string
  user: string
  password: string
  url: string
  type: string
  main: boolean
  camera_color: string
  font_color: string
  room_id: number
  room?: Room
}

interface CameraFilters {
  name: string
  room: string
  page: number
  per_page: number
}

const DEFAULT_FILTERS: CameraFilters = {
  name: '',
  room: '',
  page: 1,
  per_page: 20,
}

export default function CameraProvision() {
  const [cameras, setCameras] = useState<Camera[]>([])
  const [filtered, setFiltered] = useState<Camera[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<CameraFilters>(DEFAULT_FILTERS)
  const [showFilters, setShowFilters] = useState(false)
  const [expandedRow, setExpandedRow] = useState<number | null>(null)

  const toggleRow = (id: number) => {
    setExpandedRow((prev) => (prev === id ? null : id))
  }

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await CameraService.getAll(1, 0)
      const data: Camera[] = (res?.data as any)?.data ?? res?.data ?? []
      setCameras(Array.isArray(data) ? data : [])
    } catch {
      setError('Não foi possível carregar as câmeras.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    let result = [...cameras]

    if (filters.name.trim()) {
      result = result.filter((c) =>
        c.name.toLowerCase().includes(filters.name.trim().toLowerCase())
      )
    }

    if (filters.room.trim()) {
      result = result.filter((c) =>
        c.room?.name?.toLowerCase().includes(filters.room.trim().toLowerCase())
      )
    }

    result.sort((a, b) => {
      const nameA = a.room?.name ?? ''
      const nameB = b.room?.name ?? ''
      return nameA.localeCompare(nameB, 'pt-BR')
    })

    setTotal(result.length)

    const start = (filters.page - 1) * filters.per_page
    setFiltered(result.slice(start, start + filters.per_page))
  }, [cameras, filters])

  const applyFilters = () => {
    setFilters((f) => ({ ...f, page: 1 }))
  }

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS)
  }

  const handlePageChange = (_: unknown, newPage: number) => {
    setFilters((f) => ({ ...f, page: newPage + 1 }))
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((f) => ({ ...f, per_page: parseInt(e.target.value, 10), page: 1 }))
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h6">Provisão de Recursos</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters((v) => !v)}
          >
            Filtros
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
          >
            Atualizar
          </Button>
        </Box>
      </Box>

      {/* Filter panel */}
      <Collapse in={showFilters}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end">
            <TextField
              size="small"
              label="Nome da câmera"
              value={filters.name}
              onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
            />
            <TextField
              size="small"
              label="Nome da sala"
              value={filters.room}
              onChange={(e) => setFilters((f) => ({ ...f, room: e.target.value }))}
            />
            <Box display="flex" gap={1}>
              <Button variant="contained" size="small" onClick={applyFilters}>
                Filtrar
              </Button>
              <Button variant="outlined" size="small" onClick={resetFilters}>
                Limpar
              </Button>
            </Box>
          </Box>
        </Paper>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome da câmera</TableCell>
                  <TableCell>IP:Porta</TableCell>
                  <TableCell>Sala</TableCell>
                  <TableCell align="center">Principal</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>
                        Nenhuma câmera encontrada.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((camera) => (
                  <React.Fragment key={camera.id}>
                  <TableRow hover>
                    <TableCell>
                      <Typography variant="body2">{camera.id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {camera.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {camera.ip}:{camera.port}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {camera.room?.name ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {camera.main && (
                        <Tooltip title="Câmera principal">
                          <StarIcon fontSize="small" color="warning" />
                        </Tooltip>
                      ) || '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={expandedRow === camera.id ? 'Fechar status' : 'Ver status de provisão'}>
                        <IconButton size="small" onClick={() => toggleRow(camera.id)}>
                          {expandedRow === camera.id ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow key={`status-${camera.id}`}>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: expandedRow === camera.id ? undefined : 'none' }}>
                      <Collapse in={expandedRow === camera.id} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                          {expandedRow === camera.id && (
                            <CameraProvisionStatus cameraId={camera.id} cameraName={camera.name} />
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={(filters.page ?? 1) - 1}
            rowsPerPage={filters.per_page ?? 20}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Paper>
      )}
    </Box>
  )
}
